import { DataManager } from '@syncfusion/ej2-data';
import { ClickEventArgs, ItemModel } from '@syncfusion/ej2-navigations';
import {
    ColumnChooser,
    ContextMenu,
    Edit,
    Filter,
    FilterSettingsModel,
    Freeze,
    GridActionEventArgs,
    GridComponent,
    Inject,
    PageSettingsModel,
    QueryCellInfoEventArgs,
    Resize,
    RowDataBoundEventArgs,
    SearchSettingsModel,
    SelectionSettingsModel,
    Sort,
    SortSettingsModel,
    Toolbar,
    VirtualScroll,
} from '@syncfusion/ej2-react-grids';
import { RefObject, useRef } from 'react';
import { ReportTableRow } from '../../../utils/ReportUtils';
import { StyledTable } from '../StyledTable';
import { BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID, REPORTS_GRID_ID } from './reportsGridDialogStyles';
import { IReportsTableColumnsProps, IReportsTableTemplates, renderReportsTableColumns } from './ReportsTableColumns';

/**
 * Edit-host key must be session-stable. Do not fold column visibility / hiddenColumnFields into
 * this component's React key — Column Chooser during edit must not remount the host (Amendment C).
 * The host is created on commitEnter and destroyed on beginExit only.
 *
 * editViewDataManager is created once when this host mounts. Do not rebuild it from projected
 * session rows after every keystroke (7556691 contract).
 */
export interface IReportsBulkEditGridProps {
    gridRef: RefObject<GridComponent | null>;
    editViewRows: ReportTableRow[];
    templates: IReportsTableTemplates;
    columnsProps: Omit<IReportsTableColumnsProps, 'templates' | 'mode'>;
    height: string;
    toolbarItems: (string | ItemModel)[];
    onToolbarClick: (args: ClickEventArgs) => void;
    sortSettings: SortSettingsModel | undefined;
    pageSettings: PageSettingsModel;
    searchSettings: SearchSettingsModel;
    filterSettings: FilterSettingsModel;
    columnChooserSettings: { operator: string };
    selectionSettings: SelectionSettingsModel;
    onCreated: () => void;
    onDataBound: () => void;
    onActionBegin: (args: GridActionEventArgs) => void;
    onActionComplete: (args: GridActionEventArgs) => void;
    onQueryCellInfo: (args: QueryCellInfoEventArgs) => void;
    onRowDataBound: (args: RowDataBoundEventArgs) => void;
    onBeforeOpenColumnChooser: () => void;
}

const ReportsBulkEditGrid = ({
    gridRef,
    editViewRows,
    templates,
    columnsProps,
    height,
    toolbarItems,
    onToolbarClick,
    sortSettings,
    pageSettings,
    searchSettings,
    filterSettings,
    columnChooserSettings,
    selectionSettings,
    onCreated,
    onDataBound,
    onActionBegin,
    onActionComplete,
    onQueryCellInfo,
    onRowDataBound,
    onBeforeOpenColumnChooser,
}: IReportsBulkEditGridProps) => {
    const dataManagerRef = useRef<DataManager | null>(null);
    if (dataManagerRef.current == null && editViewRows.length > 0) {
        dataManagerRef.current = new DataManager(editViewRows);
    }

    const handleCreated = () => {
        onCreated();
        const cancelButton = gridRef.current?.element?.querySelector<HTMLElement>(`#${BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID}`);
        cancelButton?.focus();
    };

    return (
        <StyledTable
            id={REPORTS_GRID_ID}
            created={handleCreated}
            dataSource={dataManagerRef.current ?? editViewRows}
            cssClass=''
            allowResizing
            allowSorting={false}
            allowFiltering={false}
            showColumnChooser
            columnChooserSettings={columnChooserSettings}
            sortSettings={sortSettings}
            pageSettings={pageSettings}
            enableVirtualization
            enableVirtualMaskRow
            height={height}
            ref={gridRef}
            dataBound={onDataBound}
            selectionSettings={selectionSettings}
            queryCellInfo={onQueryCellInfo}
            rowDataBound={onRowDataBound}
            rowHeight={36}
            actionComplete={onActionComplete}
            actionBegin={onActionBegin}
            searchSettings={searchSettings}
            filterSettings={filterSettings}
            toolbar={toolbarItems}
            toolbarClick={onToolbarClick}
            beforeOpenColumnChooser={onBeforeOpenColumnChooser}
            loadingIndicator={{ indicatorType: 'Shimmer' }}
            editSettings={{ allowDeleting: true }}
        >
            {renderReportsTableColumns({ templates, ...columnsProps, mode: 'edit' })}
            <Inject services={[Sort, VirtualScroll, Resize, ContextMenu, Edit, Toolbar, Filter, ColumnChooser, Freeze]} />
        </StyledTable>
    );
};

export default ReportsBulkEditGrid;
